import { FeedbackEvents } from './components/FeedbackEvents';
import { SuccessCheck, useAnimatedList } from './components/feedback';
import { createContext, useCallback, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { StepUpDialog } from './components/StepUpDialog';
import { MAINTENANCE_CODE } from './api/maintenance';
import { api, ApiError } from './api/client';
import type { Me } from './api/types';

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409 && error.jobId) return '別の操作を実行中です。完了を待ってからやり直してください。';
    if (error.code === 'step_up_required') return 'ワンタイムコードでの再認証が必要です。もう一度お試しください。';
    if (error.status === 403) return 'この操作を行う権限がありません。';
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

interface Toast {
  id: number;
  text: string;
  error?: boolean;
}

interface AppContextValue {
  me: Me | undefined;
  refreshMe: () => Promise<void>;
  /** 5 分以内にワンタイムコードで再認証していなければ再認証する */
  /** fresh: 5 分間の再確認があっても、その場でコードを入れ直させる（削除・復元など） */
  ensureSteppedUp: (options?: { fresh?: boolean }) => Promise<void>;
  notify: (text: string, error?: boolean) => void;
  canOperate: boolean;
  /** 利用者を読み直す（メンテナンス中なら未ログイン扱いにしない） */
  loadMe: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me>();
  const [confirming, setConfirming] = useState(false);
  const pending = useRef<{ promise: Promise<void>; resolve: () => void; reject: (error: Error) => void } | undefined>(
    undefined,
  );
  useEffect(() => () => pending.current?.reject(new Error('再確認がキャンセルされました')), []);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const refreshMe = useCallback(async () => {
    setMe(await api.me());
  }, []);

  const loadMe = useCallback(
    () =>
      refreshMe().catch((error: unknown) => {
        // メンテナンス中は未ログイン扱いにしない（ログイン画面へ飛ばさず、戻ってから読み直す）
        if (error instanceof ApiError && error.code === MAINTENANCE_CODE) return;
        setMe({ user: null, setupRequired: false, devLogin: false, mcpUrl: '' });
      }),
    [refreshMe],
  );
  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  const notify = useCallback((text: string, error = false) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, text, error }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), error ? 7000 : 3500);
  }, []);

  const toastList = useAnimatedList(toasts, (toast) => toast.id);

  const canOperate = me?.user?.role === 'owner' || me?.user?.role === 'admin';

  const ensureSteppedUp = useCallback(
    async (options?: { fresh?: boolean }) => {
      const until = me?.steppedUpUntil ? new Date(me.steppedUpUntil).getTime() : 0;
      if (!options?.fresh && until - Date.now() > 15_000) return;
      if (me?.devLogin) {
        const result = await api.devStepUp();
        setMe((current) => (current ? { ...current, steppedUpUntil: result.steppedUpUntil } : current));
        return;
      }
      if (pending.current) return pending.current.promise;
      let resolve!: () => void;
      let reject!: (error: Error) => void;
      const promise = new Promise<void>((accept, decline) => {
        resolve = accept;
        reject = decline;
      });
      pending.current = { promise, resolve, reject };
      setConfirming(true);
      return promise;
    },
    [me?.steppedUpUntil, me?.devLogin],
  );
  const finishConfirmation = (until?: string) => {
    const request = pending.current;
    pending.current = undefined;
    setConfirming(false);
    if (until) {
      setMe((current) => (current ? { ...current, steppedUpUntil: until } : current));
      request?.resolve();
    } else request?.reject(new Error('再確認がキャンセルされました'));
  };

  return (
    <AppContext.Provider value={{ me, refreshMe, ensureSteppedUp, notify, canOperate, loadMe }}>
      <FeedbackEvents />
      {children}
      {confirming && <StepUpDialog onSuccess={finishConfirmation} onCancel={() => finishConfirmation()} />}
      <div className="toast-area" role="status" aria-live="polite">
        {toastList.items.map((toast) => (
          <div
            ref={toastList.ref}
            key={toast.id}
            className={`toast ${toastList.className(toast)}${toast.error ? ' error' : ''}`}
            inert={!toasts.some((entry) => entry.id === toast.id)}
          >
            {!toast.error && <SuccessCheck />}
            {toast.text}
            <button
              className="btn ghost small"
              aria-label="通知を閉じる"
              onClick={() => setToasts((current) => current.filter((entry) => entry.id !== toast.id))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('AppProvider is missing');
  return value;
}
